/*
  Turning a freshly connected calendar into meeting spaces.

  Once CalendarOAuthPanel has signed in, EMMS hands back every calendar on the
  account. Two things an operator can do with that list:

    import - the rooms do not exist in EMMS yet. Pick a location per entry and
             a meeting space is created with the calendar entry's own name.
    map    - the rooms already exist in EMMS. Match them to calendar entries by
             name and the calendar id is written onto the rows that are there.

  Both land on EMMS's POST /meetingspace/bulkcreate, which reports a verdict per
  entry instead of failing the whole batch - see bulkCreateFromCalendar in the
  EMMS meeting space manager.
*/

import axios from "axios";

export const PROVISION_MODES = { IMPORT: "import", MAP: "map" };

/*
  Names come from two systems that were never keeping each other in step, so
  case and stray whitespace are noise. Everything else - punctuation, digits -
  is kept, because "Room 2" and "Room 3" have to stay apart.
*/
export const normaliseRoomName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const locationLabel = (location) => {
  if (!location) return "";
  const parts = [location.city, location.building, location.floor].filter(
    (part) => part !== null && part !== undefined && String(part).trim() !== ""
  );
  return parts.join(", ") || location.id;
};

/*
  The account's calendars, kept on the credential itself.

  Stored under customProperties.calendarEntries as { "Room name": "calendarId" }
  so the Calendar Meeting ID field on a meeting space can be a dropdown instead
  of somewhere to paste an id from a provider console. Nested under its own key
  rather than written flat over customProperties because EMMS already keeps
  `expiry` there - flat, a room would be able to overwrite it, and "expiry"
  would show up in the dropdown as though it were a room.
*/
export const CALENDAR_ENTRIES_KEY = "calendarEntries";

/**
 * Build the stored map from what a sign-in returned.
 * First name wins, so the map stays stable if an account has two calendars
 * sharing a name.
 */
export const buildCalendarEntriesMap = (calendars) => {
  const entries = {};
  (calendars || []).forEach((calendar) => {
    const name = String(calendar?.name || "").trim();
    if (!name || !calendar?.id) return;
    if (entries[name]) return;
    entries[name] = calendar.id;
  });
  return entries;
};

/**
 * Read it back off a stored Calendar row, as options for a picker.
 * @returns {Array<{label: string, value: string}>} sorted by name
 */
export const calendarEntryOptions = (calendarRow) => {
  const stored = calendarRow?.customProperties?.[CALENDAR_ENTRIES_KEY];
  if (!stored || typeof stored !== "object") return [];
  return Object.entries(stored)
    .filter(([name, id]) => name && typeof id === "string" && id)
    .map(([name, id]) => ({ label: name, value: id }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Calendar ids already linked to a meeting space, so a calendar is offered to
 * at most one room.
 * @param {Array}  meetingSpaces
 * @param {string} exceptMeetingSpaceId  the room being edited, whose own link stays available
 * @returns {Set<string>}
 */
export const linkedCalendarIds = (meetingSpaces, exceptMeetingSpaceId) =>
  new Set(
    (meetingSpaces || [])
      .filter(
        (meetingSpace) =>
          meetingSpace?.calendarMeetingId &&
          meetingSpace.id !== exceptMeetingSpaceId
      )
      .map((meetingSpace) => meetingSpace.calendarMeetingId)
  );

const piggyback = async (requestBody, requestConfig) => {
  const api = localStorage.getItem("api");
  const response = await axios.post(
    `${api}/emms/piggyback`,
    requestBody,
    requestConfig
  );
  return response?.data;
};

/**
 * GET one of EMMS's plain list endpoints through ELM's proxy.
 */
export const fetchEmmsList = async ({
  path,
  emmsId,
  emmsHeaders,
  requestConfig,
}) => {
  const data = await piggyback(
    {
      path,
      query: {},
      emmsId: `${emmsId}`,
      body: {},
      requestType: "GET",
      headers: emmsHeaders,
    },
    requestConfig
  );
  return Array.isArray(data) ? data : [];
};

/**
 * Pair calendar entries with meeting spaces that carry the same name.
 *
 * A meeting space is offered to at most one entry: two calendars called
 * "Board Room" must not both claim the single room of that name, since the
 * second write would silently take the first one's calendar away.
 *
 * @returns {Array<{calendar, meetingSpace|null}>} in calendar order
 */
export const matchEntriesToMeetingSpaces = (calendars, meetingSpaces) => {
  const byName = new Map();
  (meetingSpaces || []).forEach((meetingSpace) => {
    const key = normaliseRoomName(meetingSpace?.name);
    if (!key) return;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key).push(meetingSpace);
  });

  const claimed = new Set();
  return (calendars || []).map((calendar) => {
    const candidates = byName.get(normaliseRoomName(calendar?.name)) || [];
    const meetingSpace =
      candidates.find(
        (candidate) =>
          !claimed.has(candidate.id) &&
          (!candidate.calendarMeetingId ||
            candidate.calendarMeetingId === calendar?.id)
      ) || null;
    if (meetingSpace) claimed.add(meetingSpace.id);
    return { calendar, meetingSpace };
  });
};

/**
 * Create or map meeting spaces in one call.
 *
 * @param {"import"|"map"} mode
 * @param {string} calendarCredentialId  the saved Calendar row's id
 * @param {Array}  meetingSpaces         import: {name, calendarMeetingId, locationId, capacity}
 *                                       map:    {id, calendarMeetingId, name}
 * @returns {Promise<{createdCount, updatedCount, skippedCount, failedCount, results}>}
 */
export const bulkCreateMeetingSpaces = async ({
  mode,
  calendarCredentialId,
  defaultCapacity,
  meetingSpaces,
  emmsId,
  emmsHeaders,
  requestConfig,
}) =>
  piggyback(
    {
      path: "meetingspace/bulkcreate",
      query: {},
      emmsId: `${emmsId}`,
      body: {
        mode,
        calendarCredentialId,
        defaultCapacity,
        meetingSpaces,
      },
      requestType: "POST",
      headers: emmsHeaders,
    },
    requestConfig
  );

export default bulkCreateMeetingSpaces;
